import { useEffect, useState } from "react";
import api from "../api/axios";
import { saveDraftFromBuilder } from "../api/invoices";
import { colors, spacing, borderRadius, typography, shadows, createButtonStyle } from "../styles/theme";

// Minimalny typ klienta
type ClientData = {
  id: number;
  name: string;
  address: string;
  tax_no: string;
  email?: string;
};

const InvoicePreview = () => {
  const [preview, setPreview] = useState<any>(null);
  const [clients, setClients] = useState<{ [id: number]: ClientData }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("Draft");
  // polityka/anchor pochodzą z etapu buildera (przechowane w localStorage przez DashboardUser / formularze)
const policy = (localStorage.getItem("builder_date_policy") as "rolling_30" | "previous_month") ?? "previous_month";
const anchorStart = localStorage.getItem("builder_anchor_start") ?? "";
const [issuing, setIssuing] = useState(false);

const [saving, setSaving] = useState(false);

  // Wczytaj podgląd
  useEffect(() => {
    const data = localStorage.getItem("preview_result");
    if (data) setPreview(JSON.parse(data));
  }, []);

  // Pobierz dane klientów dla każdej faktury (jeśli nie są w preview)
  useEffect(() => {
    if (!preview?.invoices) return;
    const clientIds = Array.from(
  new Set<number>(preview.invoices.map((inv: any) => Number(inv.client_id)))
);
const missing = clientIds.filter((id: number) => !(id in clients));
    if (missing.length === 0) return;
    setLoading(true);
    Promise.all(
      missing.map((id) =>
        api.get("/fakturownia-clients", { params: { id } })
          .then(res => res.data[0])
          .catch(() => null)
      )
    ).then((arr) => {
      const map: { [id: number]: ClientData } = {};
      arr.forEach((c, i) => {
        if (c && c.id) map[c.id] = c;
      });
      setClients((old) => ({ ...old, ...map }));
      setLoading(false);
    });
  }, [preview, clients]);

  if (!preview) {
    return (
      <div style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: spacing.xxl,
        textAlign: "center",
      }}>
        <div style={{
          fontSize: 64,
          marginBottom: spacing.xl,
          opacity: 0.3,
        }}>
          📋
        </div>
        <div style={{
          fontSize: typography.fontSize.xl,
          fontWeight: typography.fontWeight.semibold,
          color: colors.textSecondary,
          marginBottom: spacing.xxl,
        }}>
          Brak danych podglądu
        </div>
        <button
          onClick={() => window.location.href = "/dashboard"}
          style={createButtonStyle('primary')}
        >
          ← Wróć do dashboardu
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: spacing.xxl,
        textAlign: "center",
      }}>
        <div style={{
          fontSize: typography.fontSize.xl,
          fontWeight: typography.fontWeight.semibold,
          color: colors.textSecondary,
        }}>
          ⏳ Ładowanie danych klientów...
        </div>
      </div>
    );
  }

  const buildDraftPayload = () => {
    // 1) Jeśli kiedyś będziesz zapisywać surowy payload buildera:
    const rawBuilder = localStorage.getItem("builder_payload");
    if (rawBuilder) {
      try { return JSON.parse(rawBuilder); } catch { /* ignore */ }
    }

    // 2) NAJWAŻNIEJSZE: weź to, co wysyłałeś do preview (ma policy/anchor_start/month_key)
    const rawDraft = localStorage.getItem("draft_preview");
    if (rawDraft) {
      try {
        const arr = JSON.parse(rawDraft);
        const first = Array.isArray(arr) ? (arr[0] || {}) : {};
        return {
          type: first.type || "proforma",
          positions: Array.isArray(first.positions) ? first.positions : [],
        };
      } catch { /* ignore */ }
    }

    // 3) Ostatecznie – odbuduj z preview_result (może nie mieć anchor_start/month_key)
    const first = preview?.invoices?.[0] || {};
    const srcPositions: any[] = Array.isArray(first.positions) ? first.positions : [];

    const positions = srcPositions.map((pos: any) => {
      // Check by type field first (backend now returns this)
      if (pos.type === "revenue_percent") {
        return {
          type: "revenue_percent",
          name: pos.name,
          customer_id: pos.customer_id,
          descriptive_name: pos.descriptive_name,
          percent: pos.percent,
          minimum: pos.minimum,
          date_from: pos.date_from,
          date_to: pos.date_to,
          vat: pos.vat,
          description: pos.description ?? "",
          policy: pos.policy,
          anchor_start: pos.anchor_start,
          month_key: pos.month_key,
          gtu_code: pos.gtu_code,
        };
      }

      if (pos.type === "adjustment_percent") {
        return {
          type: "adjustment_percent",
          name: pos.name,
          source_type: pos.source_type,
          client_id: pos.client_id,
          percent: pos.percent,
          threshold: pos.threshold,
          cap_enabled: pos.cap_enabled,
          cap_amount: pos.cap_amount,
          date_from: pos.date_from,
          date_to: pos.date_to,
          vat: pos.vat,
          description: pos.description ?? "",
          gtu_code: pos.gtu_code,
        };
      }

      // Explicit handler for percent type (Allegro/Google Ads budget percent)
      if (pos.type === "percent") {
        return {
          type: "percent",
          name: pos.name,
          nickname: pos.nickname,
          percent: pos.percent,
          date_from: pos.date_from,
          date_to: pos.date_to,
          vat: pos.vat,
          description: pos.description ?? "",
          threshold: pos.threshold,
          cap_enabled: pos.cap_enabled,
          cap_amount: pos.cap_amount,
          policy: pos.policy,
          anchor_start: pos.anchor_start,
          month_key: pos.month_key,
          source_type: pos.source_type,
          gtu_code: pos.gtu_code,
        };
      }

      // Explicit handler for budget type (Allegro/Google Ads budget pass-through)
      if (pos.type === "budget") {
        return {
          type: "budget",
          name: pos.name,
          customer_id: pos.customer_id,
          descriptive_name: pos.descriptive_name,
          amount_netto: pos.amount_netto,
          date_from: pos.date_from,
          date_to: pos.date_to,
          vat: pos.vat,
          description: pos.description ?? "",
          policy: pos.policy,
          anchor_start: pos.anchor_start,
          month_key: pos.month_key,
          source_type: pos.source_type,
          gtu_code: pos.gtu_code,
        };
      }

      // Explicit handler for adjustment type (classic adjustment with podstawa_do_wyrownania)
      if (pos.type === "adjustment") {
        return {
          type: "adjustment",
          name: pos.name,
          date_from: pos.date_from,
          date_to: pos.date_to,
          podstawa_do_wyrownania: pos.podstawa_do_wyrownania,
          vat: pos.vat,
          description: pos.description ?? "",
          gtu_code: pos.gtu_code,
          ...(pos.days_in_range ? { days_in_range: pos.days_in_range } : {}),
          ...(pos.days_in_month ? { days_in_month: pos.days_in_month } : {}),
        };
      }

      const hasPercent = pos.percent != null || pos.nickname;
      const hasAdjustment = pos.podstawa_do_wyrownania != null || (pos.date_from && pos.date_to && !hasPercent && pos.type !== "adjustment_percent");
      const hasBase = pos.amount_netto != null && !hasPercent && !hasAdjustment;

      if (hasPercent) {
        return {
          type: "percent",
          name: pos.name, // NOWE
          nickname: pos.nickname,
          percent: pos.percent,
          date_from: pos.date_from,
          date_to: pos.date_to,
          vat: pos.vat,
          description: pos.description ?? "",
          threshold: pos.threshold,
          policy: pos.policy,
          anchor_start: pos.anchor_start,   // może być undefined – akceptowalne
          month_key: pos.month_key,         // j.w.
        };
      }
      if (hasAdjustment) {
        return {
          type: "adjustment",
          name: pos.name,
          date_from: pos.date_from,
          date_to: pos.date_to,
          podstawa_do_wyrownania: pos.podstawa_do_wyrownania,
          vat: pos.vat,
          description: pos.description ?? "",
          ...(pos.days_in_range ? { days_in_range: pos.days_in_range } : {}),
          ...(pos.days_in_month ? { days_in_month: pos.days_in_month } : {}),
        };
      }
      if (hasBase) {
        return { type: "base", name: pos.name, amount_netto: pos.amount_netto, vat: pos.vat, description: pos.description ?? "" };
      }
      return { type: "base", name: pos.name, amount_netto: Number(pos.amount_netto || 0), vat: Number(pos.vat || 23), description: pos.description ?? "" };
    });

    return { type: first?.type || "proforma", positions };
  };



  // Przygotuj wysyłkę faktur (tylko wymagane pola do issue)
  const prepareIssuePayload = () => {
    const company = preview.company || 'semtop';  // Default: Semtop

    const invoices = preview.invoices.map((inv: any) => ({
      client_id: inv.client_id,
      type: inv.type,
      department_name: company,  // NOWE: przekaż wybór firmy
      positions: inv.positions.map((pos: any) => ({
        name: pos.name,
        amount_netto: pos.amount_netto,
        vat: pos.vat,
        amount_brutto: pos.amount_brutto,
        description: pos.description,
        // Dodaj inne potrzebne pola, np. quantity, tylko jeśli wymagane przez backend
        ...(pos.quantity ? { quantity: pos.quantity } : {}),
        ...(pos.gtu_code ? { gtu_code: pos.gtu_code } : {}),
        // Account tracking fields (not sent to Fakturownia, only for our database)
        ...(pos.type ? { type: pos.type } : {}),
        ...(pos.nickname ? { nickname: pos.nickname } : {}),
        ...(pos.customer_id ? { customer_id: pos.customer_id } : {}),
        ...(pos.source_type ? { source_type: pos.source_type } : {}),
        ...(pos.client_id ? { client_id: pos.client_id } : {})
      }))
    }));

    return { invoices };
  };

  const handleIssue = async () => {
  setError(null);
  setIssuing(true);
  try {
    const payload = prepareIssuePayload();
    const res = await api.post("/invoices/issue", payload);

    const ids = res.data?.results
      ?.filter((r: any) => r.status === "success")
      .map((r: any) => r.invoice_id);

    if (ids && ids.length > 0) {
      alert(`Wystawiono faktury.\nID: ${ids.join(", ")}\n\nFaktury zostały automatycznie zapisane w bazie danych.`);
    } else {
      setError("Nie udało się wystawić żadnej faktury. Sprawdź dane lub spróbuj ponownie.");
    }
  } catch (e: any) {
    setError("Błąd wystawiania faktur! " + (e?.response?.data?.detail || ""));
  } finally {
    setIssuing(false);
  }
};


const handleSaveDraft = async () => {
  setError(null);
  if (!preview?.invoices?.length) {
    setError("Brak danych do zapisania jako draft.");
    return;
  }
  const fcId = Number(preview.invoices[0].client_id);
  if (!fcId) {
    setError("Brak client_id Fakturowni w podglądzie.");
    return;
  }
  if (policy === "rolling_30" && !anchorStart) {
    setError("Dla rolling_30 podaj datę startu (anchor) – wróć do ekranu dodawania i wybierz datę OD.");
    return;
  }

  const payload = buildDraftPayload();
  const company = preview.company || 'semtop';  // Default: Semtop
  const body = {
  fakturownia_client_id: Number(preview.invoices[0]?.client_id),
  name: draftName || "Draft",
  department_name: company,  // NOWE: przekaż wybór firmy
  payload, // <-- tylko to
};

  try {
    setSaving(true);
    const saved = await saveDraftFromBuilder(body);
    alert(`Zapisano draft (ID: ${saved.id})`);
  } catch (e: any) {
    const msg = e?.response?.data?.detail || "Nie udało się zapisać draftu.";
    setError(msg);
  } finally {
    setSaving(false);
  }
};

  return (
  <div style={{
    maxWidth: 1200,
    margin: "0 auto",
    padding: spacing.xxl,
  }}>
    <h2 style={{
      fontSize: typography.fontSize.xxxl,
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      marginBottom: spacing.xl,
    }}>
      📋 Podgląd faktur
    </h2>

    {error && (
      <div style={{
        padding: spacing.lg,
        marginBottom: spacing.xl,
        background: colors.errorLight,
        color: colors.error,
        border: `1px solid ${colors.error}`,
        borderRadius: borderRadius.md,
        fontWeight: typography.fontWeight.semibold,
        fontSize: typography.fontSize.base,
      }}>
        ⚠️ {error}
      </div>
    )}

    {preview.invoices.map((inv: any, idx: number) => {
      const client = clients[inv.client_id];
      return (
        <div key={idx} style={{
          marginBottom: spacing.xxl,
          padding: spacing.xl,
          background: colors.background,
          border: `1px solid ${colors.border}`,
          borderRadius: borderRadius.md,
          boxShadow: shadows.md,
        }}>
          <div style={{
            marginBottom: spacing.lg,
            padding: spacing.lg,
            background: colors.primaryLight,
            borderRadius: borderRadius.sm,
            borderLeft: `4px solid ${colors.primary}`,
          }}>
            <div style={{
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.bold,
              color: colors.textPrimary,
              marginBottom: spacing.sm,
              display: 'flex',
              alignItems: 'center',
              gap: spacing.md,
            }}>
              🧾 Faktura dla klienta
              <span style={{
                display: 'inline-block',
                padding: `${spacing.xs}px ${spacing.md}px`,
                background: (preview.company === 'semtop' || !preview.company) ? colors.primaryLight : colors.warningLight,
                color: (preview.company === 'semtop' || !preview.company) ? colors.primary : colors.warning,
                borderRadius: borderRadius.sm,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.semibold,
              }}>
                {(preview.company === 'semtop' || !preview.company) ? 'Semtop' : 'AdTarget'}
              </span>
            </div>
            {client ? (
              <div style={{
                fontSize: typography.fontSize.base,
                color: colors.textPrimary,
              }}>
                <div style={{ marginBottom: spacing.xs }}>
                  <strong>{client.name}</strong>
                </div>
                <div style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.textSecondary,
                  lineHeight: 1.6,
                }}>
                  {client.address} • NIP: {client.tax_no}
                  {client.email && ` • ${client.email}`}
                </div>
                <div style={{
                  marginTop: spacing.sm,
                  fontSize: typography.fontSize.sm,
                  color: colors.textSecondary,
                }}>
                  ID: {client.id} • Typ: <span style={{ fontWeight: typography.fontWeight.semibold }}>{inv.type}</span>
                </div>
              </div>
            ) : (
              <div style={{
                fontSize: typography.fontSize.base,
                color: colors.textSecondary,
              }}>
                ID klienta: {inv.client_id} • Typ: {inv.type}
              </div>
            )}
          </div>

          <div style={{
            overflowX: "auto",
            marginTop: spacing.lg,
          }}>
            <table style={{
              borderCollapse: "collapse",
              width: "100%",
              fontSize: typography.fontSize.sm,
              background: colors.backgroundAlt,
              borderRadius: borderRadius.sm,
              overflow: "hidden",
            }}>
              <thead>
                <tr style={{
                  background: colors.primary,
                  color: "white",
                }}>
                  <th style={{
                    padding: spacing.md,
                    textAlign: "left",
                    fontWeight: typography.fontWeight.semibold,
                    fontSize: typography.fontSize.sm,
                  }}>Nazwa pozycji</th>
                  <th style={{
                    padding: spacing.md,
                    textAlign: "right",
                    fontWeight: typography.fontWeight.semibold,
                    fontSize: typography.fontSize.sm,
                  }}>Kwota netto</th>
                  <th style={{
                    padding: spacing.md,
                    textAlign: "right",
                    fontWeight: typography.fontWeight.semibold,
                    fontSize: typography.fontSize.sm,
                  }}>VAT (%)</th>
                  <th style={{
                    padding: spacing.md,
                    textAlign: "right",
                    fontWeight: typography.fontWeight.semibold,
                    fontSize: typography.fontSize.sm,
                  }}>Kwota brutto</th>
                  <th style={{
                    padding: spacing.md,
                    textAlign: "left",
                    fontWeight: typography.fontWeight.semibold,
                    fontSize: typography.fontSize.sm,
                  }}>Opis</th>
                  <th style={{
                    padding: spacing.md,
                    textAlign: "center",
                    fontWeight: typography.fontWeight.semibold,
                    fontSize: typography.fontSize.sm,
                  }}>GTU</th>
                  <th style={{
                    padding: spacing.md,
                    textAlign: "left",
                    fontWeight: typography.fontWeight.semibold,
                    fontSize: typography.fontSize.sm,
                  }}>Szczegóły</th>
                  <th style={{
                    padding: spacing.md,
                    textAlign: "center",
                    fontWeight: typography.fontWeight.semibold,
                    fontSize: typography.fontSize.sm,
                  }}>Brakujące dni</th>
                </tr>
              </thead>
            <tbody>
              {inv.positions.map((pos: any, i: number) => {
                const missing: string[] = Array.isArray(pos.missing_days) ? pos.missing_days : [];
                return (
                  <tr key={i} style={{
                    borderBottom: `1px solid ${colors.border}`,
                    transition: "background 0.2s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = colors.gray50; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                    <td style={{
                      padding: spacing.md,
                      fontSize: typography.fontSize.sm,
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.textPrimary,
                    }}>{pos.name}</td>
                    <td style={{
                      padding: spacing.md,
                      textAlign: "right",
                      fontSize: typography.fontSize.sm,
                      fontWeight: typography.fontWeight.bold,
                      color: colors.success,
                    }}>{pos.amount_netto ?? ""}</td>
                    <td style={{
                      padding: spacing.md,
                      textAlign: "right",
                      fontSize: typography.fontSize.sm,
                      color: colors.textPrimary,
                    }}>{pos.vat}</td>
                    <td style={{
                      padding: spacing.md,
                      textAlign: "right",
                      fontSize: typography.fontSize.sm,
                      fontWeight: typography.fontWeight.bold,
                      color: colors.textPrimary,
                    }}>{pos.amount_brutto ?? ""}</td>
                    <td style={{
                      padding: spacing.md,
                      fontSize: typography.fontSize.xs,
                      color: colors.textSecondary,
                    }}>{pos.description ?? ""}</td>
                    {/* GTU */}
                    <td style={{
                      padding: spacing.md,
                      textAlign: "center",
                      fontSize: typography.fontSize.xs,
                      color: pos.gtu_code ? colors.textPrimary : colors.textSecondary,
                      fontWeight: pos.gtu_code ? typography.fontWeight.semibold : typography.fontWeight.normal,
                    }}>
                      {pos.gtu_code || "—"}
                    </td>
                    {/* Szczegóły */}
                    <td style={{
                      padding: spacing.md,
                      fontSize: typography.fontSize.xs,
                      color: colors.textSecondary,
                    }}>
                      {pos.type === "adjustment" && (
                        <>
                          <div><b>Dni:</b> {pos.days_in_range}</div>
                          <div><b>Podstawa netto:</b> {pos.podstawa_do_wyrownania ?? "-"}</div>
                          <div><b>Okres:</b> {pos.date_from ?? "-"} – {pos.date_to ?? "-"}</div>
                        </>
                      )}
                      {pos.type === "adjustment_percent" && (
                        <>
                          <div>
                            <b>Budżet klienta:</b> {Number((pos as any).total_cost || 0).toFixed(2)} zł
                            {(pos as any).cap_enabled && (pos as any).cap_amount && ` (${Number((pos as any).cap_amount).toFixed(2)} zł max)`}
                          </div>
                          <div><b>{(pos as any).source_type === "google_ads" ? "Google Ads" : "Allegro Ads"}:</b> {(pos as any).display_name || (pos as any).client_id || "-"}</div>
                          <div><b>Okres:</b> {pos.date_from ?? "-"} – {pos.date_to ?? "-"}</div>
                          <div><b>Procent:</b> {(pos as any).percent ?? "-"}</div>
                          <div>
                            <b>Próg (PLN):</b> {(pos as any).threshold ?? 0}
                            {((pos as any).threshold ?? 0) === 0 && <span style={{ marginLeft: "8px", color: "#666", fontStyle: "italic" }}>(cały budżet)</span>}
                          </div>
                        </>
                      )}
                      {pos.type === "percent" && (
                        <>
                          <div>
                            <b>Budżet klienta:</b> {Number(pos.total_cost).toFixed(2)} zł
                            {pos.cap_enabled && pos.cap_amount && ` (${Number(pos.cap_amount).toFixed(2)} zł max)`}
                          </div>
                          <div><b>{pos.source_type === "google_ads" ? "Google Ads" : "Allegro Ads"}:</b> {pos.nickname ?? "-"}</div>
                          <div><b>Okres:</b> {pos.date_from ?? "-"} – {pos.date_to ?? "-"}</div>
                          <div><b>Procent:</b> {pos.percent ?? "-"}</div>
                          <div>
                            <b>Próg (PLN):</b> {pos.threshold ?? 0}
                            {(pos.threshold ?? 0) === 0 && <span style={{ marginLeft: "8px", color: "#666", fontStyle: "italic" }}>(cały budżet)</span>}
                          </div>
                          <div><b>Tryb:</b> {pos.policy === "rolling_30"
                            ? "Umowa miesięczna"
                            : pos.policy === "previous_month"
                            ? "Czas nieokreślony"
                            : (pos.policy ?? "-")}</div>
                        </>
                      )}
                      {pos.type === "revenue_percent" && (
                        <>
                          <div><b>Obrót (conversions_value):</b> {Number(pos.conversions_value).toFixed(2)}</div>
                          <div><b>{(pos as any).source_type === "allegro" ? "Allegro Ads" : "Google Ads"}:</b> {pos.descriptive_name ?? "-"}</div>
                          <div><b>Okres:</b> {pos.date_from ?? "-"} – {pos.date_to ?? "-"}</div>
                          <div><b>Procent:</b> {pos.percent ?? "-"}%</div>
                          <div><b>Minimum (PLN):</b> {pos.minimum ?? 0}</div>
                          <div><b>Wyliczone:</b> {Number(pos.calculated_amount || 0).toFixed(2)} PLN</div>
                          <div><b>Użyte (MAX):</b> {Number(pos.amount_netto).toFixed(2)} PLN</div>
                          <div><b>Tryb:</b> {pos.policy === "rolling_30"
                            ? "Umowa miesięczna"
                            : pos.policy === "previous_month"
                            ? "Czas nieokreślony"
                            : (pos.policy ?? "-")}</div>
                        </>
                      )}
                      {pos.type === "budget" && (
                        <>
                          <div><b>Konto:</b> {pos.descriptive_name ?? "-"}</div>
                          <div><b>Źródło:</b> {(pos as any).source_type === "allegro" ? "Allegro Ads" : "Google Ads"}</div>
                          <div><b>Okres:</b> {pos.date_from ?? "-"} – {pos.date_to ?? "-"}</div>
                          <div><b>Budżet wydany:</b> {Number(pos.total_cost_brutto || 0).toFixed(2)} zł brutto</div>
                          <div><b>Kwota netto (÷1.23):</b> {Number(pos.amount_netto).toFixed(2)} zł</div>
                          <div><b>Tryb:</b> {pos.policy === "rolling_30"
                            ? "Umowa miesięczna"
                            : pos.policy === "previous_month"
                            ? "Czas nieokreślony"
                            : pos.policy === "custom"
                            ? "Custom"
                            : (pos.policy ?? "-")}</div>
                        </>
                      )}
                    </td>

                    {/* Brakujące dni */}
                    <td style={{
                      padding: spacing.md,
                      textAlign: "center",
                      fontSize: typography.fontSize.xs,
                    }}>
                      {(pos.type !== "percent" && pos.type !== "revenue_percent" && pos.type !== "adjustment_percent")
                        ? <span style={{ color: colors.textSecondary }}>—</span>
                        : missing.length === 0
                          ? <span style={{
                              color: colors.success,
                              fontWeight: typography.fontWeight.semibold,
                            }}>✓ Brak</span>
                          : (
                            <div style={{
                              display: "inline-block",
                              padding: `${spacing.xs}px ${spacing.sm}px`,
                              background: colors.warningLight,
                              color: colors.warning,
                              borderRadius: borderRadius.sm,
                              fontWeight: typography.fontWeight.semibold,
                            }}>
                              <div><b>{missing.length}</b> {missing.length === 1 ? "dzień" : "dni"}</div>
                              <div style={{
                                maxHeight: 100,
                                overflowY: "auto",
                                marginTop: spacing.xs,
                                fontSize: typography.fontSize.xs,
                                whiteSpace: "normal",
                              }}>
                                {missing.join(", ")}
                              </div>
                            </div>
                          )
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      );
    })}

    <div style={{
      display: "flex",
      gap: spacing.lg,
      marginTop: spacing.xxl,
      flexWrap: "wrap",
    }}>
      <button
        onClick={handleIssue}
        disabled={issuing}
        style={{
          ...createButtonStyle('success', issuing),
          flex: "1 1 auto",
          fontSize: typography.fontSize.lg,
          padding: `${spacing.md}px ${spacing.xxl}px`,
        }}
      >
        {issuing ? "⏳ Wystawiam…" : "✅ Wystaw faktury"}
      </button>
    </div>

    <div style={{
      marginTop: spacing.xl,
      padding: spacing.xl,
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.md,
      background: colors.backgroundAlt,
    }}>
      <div style={{
        fontSize: typography.fontSize.lg,
        fontWeight: typography.fontWeight.bold,
        color: colors.textPrimary,
        marginBottom: spacing.md,
      }}>
        💾 Zapisz jako draft
      </div>

      <div style={{
        display: "flex",
        gap: spacing.md,
        flexWrap: "wrap",
        alignItems: "center",
        marginBottom: spacing.md,
      }}>
        <label style={{
          fontSize: typography.fontSize.sm,
          fontWeight: typography.fontWeight.semibold,
          color: colors.textPrimary,
        }}>
          Nazwa:
        </label>
        <input
          value={draftName}
          onChange={e => setDraftName(e.target.value)}
          style={{
            flex: "1 1 300px",
            padding: spacing.sm,
            fontSize: typography.fontSize.sm,
            border: `1px solid ${colors.border}`,
            borderRadius: borderRadius.sm,
            outline: "none",
          }}
          onFocus={(e) => { e.target.style.borderColor = colors.primary; }}
          onBlur={(e) => { e.target.style.borderColor = colors.border; }}
        />

        <button
          onClick={handleSaveDraft}
          disabled={saving}
          style={{
            ...createButtonStyle('primary', saving),
            flex: "0 0 auto",
          }}
        >
          {saving ? "⏳ Zapisuję…" : "💾 Zapisz draft"}
        </button>
      </div>

      <div style={{
        padding: spacing.md,
        background: colors.infoLight,
        border: `1px solid ${colors.info}`,
        borderRadius: borderRadius.sm,
        fontSize: typography.fontSize.sm,
        color: colors.textSecondary,
        lineHeight: 1.6,
      }}>
        ℹ️ Pozycja "procent" oraz "procent od obrotu" (nieokreślony/miesięczna) zapisana w drafcie będzie automatycznie przesuwać datę do najbliższego okresu, który w pełni minął.<br/>
        „Podstawa" i „Wyrównanie" nie przesuwają dat, bo ich nie mają.<br/>
        <strong>Jako draft zostanie zapisana tylko pierwsza faktura!</strong>
      </div>
    </div>

    <div style={{
      marginTop: spacing.xl,
      display: "flex",
      justifyContent: "center",
    }}>
      <button
        onClick={() => window.location.href = "/dashboard"}
        style={{
          ...createButtonStyle('secondary'),
          fontSize: typography.fontSize.base,
        }}
      >
        ← Wróć do edycji faktur
      </button>
    </div>
  </div>
);
};

export default InvoicePreview;